/**
 * Dashboard-only constants — the repo-specific vocabulary centralised in ONE
 * place so nothing is duplicated across feature folders.
 *
 * Anything shared across apps (canonical field keys, labels, view modes, chain
 * constants, risk buckets, …) is intentionally NOT here — it lives in
 * `@authe/fe-commons` and is re-exported through `./index` so callers have a
 * single import surface without us forking the shared vocabulary.
 */
import { appEnv } from "./env";

/** Source-platform slug stamped on every API request + Auth0 `application` claim. */
export const APPLICATION_SLUG = "dashboard" as const;

/** Gateway auth tier for the dashboard (matches APP_REGISTRY `dashboard.type`). */
export const APPLICATION_AUTH_TYPE = "external" as const;

/**
 * Canonical resource names. These double as RBAC resource ids (the subject the
 * Casbin policy enforces on) AND the API resource path segments, so the route,
 * the permission check and the data hook never drift.
 */
export const RESOURCES = {
  overview: "dashboard",
  transactions: "transactions",
  addresses: "addresses",
  entities: "entities",
  groups: "groups",
  // Also the RBAC resource for saved views (and so for Investigations, which
  // ARE saved views): `saved-views-api` authorizes `shared-graphs:*` rather than
  // minting a `saved-views:*` family, because the dashboard tiers already carry
  // these scopes. There is deliberately no `savedViews` id here — it is not in
  // the scope catalog, so gating on it can only ever deny.
  graphs: "shared-graphs",
  askAi: "ask-ai",
  stats: "stats",
  // metadata-registry-api (internal tier). Backs the Analytics feature — the
  // Datadog-style dashboards over the decoded-metadata EAV/materialized views.
  // The `:analytics` action is the read verb Reader-tier tokens carry; authoring
  // dashboards/facets/views/policy gates on create/edit/delete of this resource.
  metadataRegistry: "metadata-registry",
  activity: "activity-feed",
  pipelines: "pipelines",
  // Run jobs are part of the pipeline platform and the backend reuses the
  // `pipelines` scopes (pipelines:read / pipelines:process), so the Runs feature
  // is gated on the SAME RBAC resource rather than a separate (non-existent) one.
  runs: "pipelines",
  tracers: "tracers",
  alerts: "actions",
  watches: "watches",
  contracts: "contracts",
  /** Backend permission resource for the ABI registry (abis:list/show/create/edit). */
  abis: "abis",
  // proxy-mgmt. A separate resource from `abis` because the two assertions carry
  // different blast radii: an ABI override changes how a contract is *read*, while
  // a proxy pin changes which contract is read at all — and `proxy:delete` purges
  // the shared discovery rows every tenant resolves through.
  proxy: "proxy",
  integrations: "integrations",
  // Org/team membership (permissions-mgmt-api). `team` gates the Team page + the
  // "Invite people" flow on the customer-facing user scopes (users:list/create);
  // `roles` gates the org role picker on roles:list. Internal staff management
  // uses the separate `internal-users:*` scopes, never exposed here.
  team: "users",
  roles: "roles",
  // Team lifecycle (teams-mgmt-api). Gates "New team" on teams:create; team
  // membership/roles/invites are enforced per-team by the panel's own scopes.
  teams: "teams",
  wallets: "wallets",
  settings: "settings",
  // Billing/plan surface (subscriptions-mgmt-api). Gates the Settings "Billing"
  // tab on the read verb the token carries (subscriptions:list / :show).
  subscriptions: "subscriptions",
  // Tenancy admin (management-api). Back the rename/suspend forms with the same
  // RBAC resources the backend scopes use (organizations:edit / tenants:edit /
  // customers:edit), so the UI only offers an action the token actually grants.
  organizations: "organizations",
  tenants: "tenants",
  customers: "customers",
} as const;

export type ResourceName = (typeof RESOURCES)[keyof typeof RESOURCES];

/** Route paths, derived from the resource names so URLs stay consistent. */
export const ROUTES = {
  overview: "/",
  gettingStarted: "/getting-started",
  transactions: "/transactions",
  transaction: (id: string) => `/transactions/${id}`,
  addresses: "/addresses",
  address: (id: string) => `/addresses/${id}`,
  catalog: "/catalog",
  entities: "/entities",
  entity: (id: string) => `/entities/${id}`,
  groups: "/groups",
  group: (id: string) => `/groups/${id}`,
  graphs: "/graphs",
  graph: (id: string) => `/graphs/${id}`,
  investigations: "/investigations",
  investigation: (id: string) => `/investigations/${id}`,
  contracts: "/contracts",
  contract: (chainId: string, address: string) => `/contracts/${chainId}/${address}`,
  askAi: "/ask-ai",
  stats: "/stats",
  analytics: "/analytics",
  // Static child of /analytics — the Datadog-style transaction explorer. Must
  // be declared before the `$dashboardId` param route so it resolves as a
  // literal segment, not a dashboard id.
  analyticsExplorer: "/analytics/explorer",
  // Sibling static child: the protocol-definition catalogue + this tenant's
  // decoding overrides (the authoring end of the explorer's scope markers).
  analyticsProtocols: "/analytics/protocols",
  analyticsDashboard: (id: string) => `/analytics/${id}`,
  activity: "/activity",
  pipelines: "/pipelines",
  pipeline: (id: string) => `/pipelines/${id}`,
  runs: "/runs",
  run: (id: string) => `/runs/${id}`,
  tracers: "/tracers",
  tracer: (id: string) => `/tracers/${id}`,
  alerts: "/alerts",
  watches: "/watches",
  wallet: "/wallet",
  team: "/team",
  // Deep-link landing for the branded team-invite email (carries `?token=`).
  // Not a sidebar item — reached only from the invitation email.
  acceptTeamInvite: "/teams/accept-invite",
  settings: "/settings",
  settingsBilling: "/settings/billing",
  profile: "/profile",
} as const;

/** Default page size for paginated DynamoDB-backed lists (no scan, cursor-based). */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * Outbound links surfaced across the app (docs, sales/contact, privacy).
 *
 * Derived from the ACTIVE environment's base domain (`appEnv.apiBaseDomain`) so
 * every deploy points at its OWN site instead of always jumping to production:
 * prod → `authe.io`, sandbox → `sandbox.authe.io`, etc. In prod these resolve to
 * the exact same URLs as before (`docs.authe.io`, `authe.io/contact-sales`,
 * `authe.io/privacy`). Centralised so no component hard-codes a URL.
 */
export const EXTERNAL_LINKS = {
  docs: `https://docs.${appEnv.apiBaseDomain}`,
  discuss: `https://${appEnv.apiBaseDomain}/contact-sales`,
  privacy: `https://${appEnv.apiBaseDomain}/privacy`,
} as const;

/** Persisted UI-language preference (localStorage key + supported set). */
export const LANGUAGE_STORAGE_KEY = "dashboard-language";

export interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

/**
 * Languages offered in the header switcher. This sets the *preference* only;
 * full message translation arrives when an i18n provider is wired (the
 * `@authe/fe-commons/i18n` error-copy bundle is the first consumer).
 */
export const LANGUAGES: LanguageOption[] = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
];
